import { useEffect, useRef, useState } from 'react';
import { apiWithGetHeader } from '@/service/api';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';
import { getUserAccessToken } from '@/utilities/setting';
import { API_USERS_CHECK_AVAILABILITY, API_AGENTS_CHECK_AVAILABILITY } from '@/service/url';
import { cn } from '@/lib/utils/cn';

async function silentGet(url: string, params: Record<string, string | number>) {
  const res = await apiWithGetHeader(BACKEND_API_BASE_URL, params, getUserAccessToken()).get(url, {
    timeout: 10000,
  });
  return res?.data?.data ?? {};
}

export async function isUsernameAvailable(username: string, excludeUserId?: number | string | null) {
  const data = await silentGet(API_USERS_CHECK_AVAILABILITY, {
    username,
    ...(excludeUserId != null && excludeUserId !== '' ? { exclude_user_id: excludeUserId } : {}),
  });
  return data?.username?.available !== false;
}


export async function isUserEmailAvailable(email: string, excludeUserId?: number | string | null) {
  const data = await silentGet(API_USERS_CHECK_AVAILABILITY, {
    email,
    ...(excludeUserId != null && excludeUserId !== '' ? { exclude_user_id: excludeUserId } : {}),
  });
  return data?.email?.available !== false;
}


export async function isAgentNameAvailable(name: string, excludeAgentId?: number | string | null) {
  const data = await silentGet(API_AGENTS_CHECK_AVAILABILITY, {
    agent_name: name,
    ...(excludeAgentId != null && excludeAgentId !== '' ? { exclude_agent_id: excludeAgentId } : {}),
  });
  return data?.agent_name?.available !== false;
}


export async function isAgentNumberAvailable(number: string, excludeAgentId?: number | string | null) {
  const data = await silentGet(API_AGENTS_CHECK_AVAILABILITY, {
    agent_number: number,
    ...(excludeAgentId != null && excludeAgentId !== '' ? { exclude_agent_id: excludeAgentId } : {}),
  });
  return data?.agent_number?.available !== false;
}

export async function isAgentEmailAvailable(email: string, excludeAgentId?: number | string | null) {
  const data = await silentGet(API_AGENTS_CHECK_AVAILABILITY, {
    email_id: email,
    ...(excludeAgentId != null && excludeAgentId !== '' ? { exclude_agent_id: excludeAgentId } : {}),
  });
  return data?.email_id?.available !== false;
}

export type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error';

interface Options {
  enabled?: boolean;
  originalValue?: string | null;
  excludeId?: number | string | null;
  delay?: number;
}

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();


function useAvailability(
  value: string,
  check: (value: string, excludeId?: number | string | null) => Promise<boolean>,
  { enabled = true, originalValue = null, excludeId = null, delay = 500 }: Options = {},
): AvailabilityStatus {
  const [status, setStatus] = useState<AvailabilityStatus>('idle');
  const checkRef = useRef(check);
  checkRef.current = check;

  useEffect(() => {
    const trimmed = String(value ?? '').trim();
    if (!enabled || !trimmed || (originalValue != null && norm(trimmed) === norm(originalValue))) {
      setStatus('idle');
      return undefined;
    }
    setStatus('checking');
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const free = await checkRef.current(trimmed, excludeId);
        if (!cancelled) setStatus(free ? 'available' : 'taken');
      } catch {
        if (!cancelled) setStatus('error');
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, enabled, originalValue, excludeId, delay]);

  return status;
}

export const useUsernameAvailability = (username: string, opts?: Options) =>
  useAvailability(username, isUsernameAvailable, opts);

export const useUserEmailAvailability = (email: string, opts?: Options) =>
  useAvailability(email, isUserEmailAvailable, opts);

export const useAgentNameAvailability = (name: string, opts?: Options) =>
  useAvailability(name, isAgentNameAvailable, opts);

export const useAgentNumberAvailability = (number: string, opts?: Options) =>
  useAvailability(number, isAgentNumberAvailable, opts);

export const useAgentEmailAvailability = (email: string, opts?: Options) =>
  useAvailability(email, isAgentEmailAvailable, opts);

interface AvailabilityHintProps {
  status: AvailabilityStatus;
  takenMessage: string;
  availableMessage?: string;
  className?: string;
}


export function AvailabilityHint({
  status,
  takenMessage,
  availableMessage,
  className,
}: AvailabilityHintProps) {
  if (status === 'taken') {
    return (
      <p role="alert" className={cn('mt-1 text-xs text-destructive', className)}>
        {takenMessage}
      </p>
    );
  }
  if (status === 'checking') {
    return (
      <p className={cn('mt-1 text-xs text-muted-foreground', className)} aria-live="polite">
        Checking availability…
      </p>
    );
  }
  if (status === 'available' && availableMessage) {
    return (
      <p className={cn('mt-1 text-xs text-emerald-600', className)} aria-live="polite">
        {availableMessage}
      </p>
    );
  }
  return null;
}